import { Injectable } from '@angular/core';
import { Color, Style } from '../models/new/style';
import {
	AspectRatio,
	MediaFormat,
	Shot,
	ShotApprovalStatus,
	ShotType
} from '../models/new/shot';
import { MediaShareColorShots, MediaShareDetail, MediaShareMenuBrand } from '../models/new/media-share-detail';
import { Brand } from '../models/new/brand.model';
import { StyleGroup } from '../models/new/style-group';
import { Collection } from '../models/new/collection';

@Injectable({
	providedIn: 'root'
})
export class MediaShareDetailMapper {
	toMediaShareDetails(nodes: any[]): MediaShareDetail[] {
		return (nodes || []).map((node) => this.toMediaShareDetail(node));
	}

	/** Albero Brand > Collection > Style group > prodotto, ordinato per etichetta e codice. */
	toMediaShareMenu(details: MediaShareDetail[]): MediaShareMenuBrand[] {
		const brandLabel = (d: MediaShareDetail) => d.style.brand?.name?.trim() || 'Other brands';
		const collectionLabel = (d: MediaShareDetail) => d.style.collection?.name?.trim() || 'No collection';
		const styleGroupLabel = (d: MediaShareDetail) => d.style.styleGroup?.name?.trim() || 'No style group';
		const sortKey = (d: MediaShareDetail) => `${brandLabel(d)}|${collectionLabel(d)}|${styleGroupLabel(d)}|${d.style.code}`;

		const menu: MediaShareMenuBrand[] = [];

		[...details]
			.sort((a, b) => sortKey(a).localeCompare(sortKey(b)))
			.forEach((detail) => {
				let brand = menu.find((b) => b.label === brandLabel(detail));
				if (!brand) {
					brand = { key: `b:${brandLabel(detail)}`, label: brandLabel(detail), collections: [] };
					menu.push(brand);
				}

				let collection = brand.collections.find((c) => c.label === collectionLabel(detail));
				if (!collection) {
					collection = { key: `${brand.key}/c:${collectionLabel(detail)}`, label: collectionLabel(detail), styleGroups: [] };
					brand.collections.push(collection);
				}

				let styleGroup = collection.styleGroups.find((g) => g.label === styleGroupLabel(detail));
				if (!styleGroup) {
					styleGroup = { key: `${collection.key}/g:${styleGroupLabel(detail)}`, label: styleGroupLabel(detail), products: [] };
					collection.styleGroups.push(styleGroup);
				}

				styleGroup.products.push(detail);
			});

		return menu;
	}

	private toMediaShareDetail(node: any): MediaShareDetail {
		const schemas = Array.isArray(node.style?.schema) ? node.style.schema : node.style?.schema ? [node.style.schema] : [];
		const collection = [...schemas].reverse().find((schema: any) => schema.collection)?.collection;

		return {
			opened: true,
			style: new Style({
				modelId: node.style?.modelId,
				code: node.style?.code,
				name: node.style?.name,
				brand: node.style?.brand ? new Brand({ id: node.style.brand.id, name: node.style.brand.name }) : undefined,
				styleGroup: node.style?.styleGroup
					? new StyleGroup({ id: node.style.styleGroup.id, name: node.style.styleGroup.name })
					: undefined,
				collection: collection ? new Collection({ id: collection.id, name: collection.name }) : undefined
			}),
			colors: (node.colors || []).map((colorNode: any) => this.toColorShots(colorNode))
		};
	}

	private toColorShots(colorNode: any): MediaShareColorShots {
		const shots = (colorNode.shots || []).map((shot: any) => this.toShot(shot));

		return {
			mediaShareLinkDetailId: colorNode.mediaShareLinkDetailId,
			color: new Color({
				id: colorNode.color?.id,
				code: colorNode.color?.code,
				name: colorNode.color?.finalColorName || colorNode.color?.name
			}),
			shots,
			isAi: shots.some((shot: Shot) => !!shot.isAi)
		};
	}

	private toShot(shot: any): Shot {
		return new Shot({
			id: shot.shotId,
			mandatory: shot.mandatory,
			imgPath: shot.imgPath,
			isAi: shot.isAi,
			estimatedFileName: shot.estimatedFileName,
			shotApprovalStatus: shot.shotApprovalStatusId as ShotApprovalStatus,
			mediaFormat: shot.mediaFormat
				? new MediaFormat({ id: shot.mediaFormat.id, name: shot.mediaFormat.label })
				: undefined,
			aspectRatio: shot.aspectRatio
				? new AspectRatio({ id: shot.aspectRatio.id, label: shot.aspectRatio.label })
				: undefined,
			shotType: shot.shotType ? new ShotType({ id: shot.shotType.id, name: shot.shotType.label }) : undefined
		});
	}
}
